// src/features/organisation/components/departments/DepartmentDetailForm.tsx
import React, { useMemo } from 'react';
import { Briefcase } from 'lucide-react';
import { Department } from '../../../../types/organisation';
import { useSites } from '../../hooks/useSites';
import {
  EntityFormLayout,
  EntityFormHeader,
  EntityFormFooter,
  EditableField
} from '../../../../components/common/forms';
import { useEntityForm, FieldConfig } from '../../../../components/common/hooks';
import { apiClient } from '../../../../services/api/client';

interface DepartmentDetailFormProps {
  departmentId: number | null;
  onClose: () => void;
  onSave?: (department: Department) => void;
}

const DepartmentDetailForm: React.FC<DepartmentDetailFormProps> = ({
  departmentId,
  onClose,
  onSave: onSaveCallback,
}) => {
  const { sites } = useSites();
  const [loading, setLoading] = React.useState(!!departmentId);
  const [department, setDepartment] = React.useState<Department | null>(null);

  React.useEffect(() => {
    if (departmentId) {
      setLoading(true);
      apiClient.get(`/organisation/departments/${departmentId}`)
        .then((data) => setDepartment(data))
        .finally(() => setLoading(false));
    } else {
      setDepartment(null);
    }
  }, [departmentId]);

  const fields: FieldConfig[] = useMemo(() => [
    {
      name: 'departmentCode',
      label: 'Code du département',
      type: 'text',
      required: true,
      maxLength: 20,
      disabled: !!departmentId,
    },
    {
      name: 'departmentName',
      label: 'Nom du département',
      type: 'text',
      required: true,
      maxLength: 255,
    },
    {
      name: 'siteId',
      label: 'Site',
      type: 'select',
      required: true,
      options: sites.map(s => ({ value: s.id, label: s.siteName })),
      render: (value) => sites.find(s => s.id === value)?.siteName || 'Non défini',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      maxLength: 1000,
      colSpan: 2,
    },
  ], [sites, departmentId]);

  const transformToFormData = (dept: Department | null) => {
    if (!dept) return { departmentCode: '', departmentName: '', siteId: null, description: '' };
    return {
      departmentCode: dept.departmentCode,
      departmentName: dept.departmentName,
      siteId: dept.site?.id,
      description: dept.description || '',
    };
  };

  const handleSave = async (formData: Record<string, unknown>) => {
    let saved: Department;
    if (departmentId) {
      saved = await apiClient.put(`/organisation/departments/${departmentId}`, formData);
    } else {
      saved = await apiClient.post('/organisation/departments', formData);
    }
    onSaveCallback?.(saved);
    onClose();
    return saved;
  };

  const {
    formData,
    hasChanges,
    saving,
    errors,
    handleFieldChange,
    startEditing,
    stopEditing,
    isEditing,
    handleSave: onFormSave,
  } = useEntityForm({
    entity: department,
    fields,
    onSave: handleSave,
    transformToFormData,
  });

  return (
    <EntityFormLayout loading={loading} maxWidth="4xl">
      <EntityFormHeader
        title={department ? department.departmentName : 'Nouveau département'}
        subtitle={department ? `Code: ${department.departmentCode}` : undefined}
        icon={Briefcase}
        hasChanges={hasChanges}
        saving={saving}
        onSave={onFormSave}
        onClose={onClose}
      />

      <div className="flex-1 overflow-y-auto p-6">
        {formData && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations</h3>
            <div className="grid grid-cols-2 gap-4">
              {fields.map(field => (
                <div key={field.name} className={field.colSpan === 2 ? 'col-span-2' : ''}>
                  <EditableField
                    field={field}
                    value={formData[field.name]}
                    isEditing={isEditing(field.name)}
                    onStartEdit={() => startEditing(field.name)}
                    onStopEdit={() => stopEditing(field.name)}
                    onChange={(value) => handleFieldChange(field.name, value)}
                    error={errors[field.name]}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <EntityFormFooter helpText="💡 Double-cliquez sur un champ pour le modifier." />
    </EntityFormLayout>
  );
};

export default DepartmentDetailForm;
